import React, { useState } from 'react';
import { Menu, X, Globe, Phone, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate, useLocation } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const navigationItems = [
    { name: 'Home', href: '/', anchor: null, type: 'home' },
    { name: 'Services', href: '/', anchor: 'services', type: 'anchor' },
    { name: 'Technologies', href: '/', anchor: 'technologies', type: 'anchor' },
    { name: 'About', href: '/', anchor: 'about', type: 'anchor' },
    { name: 'Testimonials', href: '/', anchor: 'testimonials', type: 'anchor' },
    { name: 'Careers', href: '/careers', anchor: null, type: 'link' },
    { name: 'Contact', href: '/', anchor: 'contact', type: 'anchor' }
  ];

  const handleNavClick = (e, item) => {
    e.preventDefault();
    
    if (item.type === 'home') {
      // Navigate to homepage and scroll to top
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else if (item.type === 'link') {
      // Navigate to the route directly
      navigate(item.href);
    } else if (item.type === 'anchor') {
      // If not on homepage, navigate there first then scroll
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const element = document.getElementById(item.anchor);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        // Already on homepage, just scroll
        const element = document.getElementById(item.anchor);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
    
    setIsMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center cursor-pointer" onClick={(e) => handleNavClick(e, { type: 'home', href: '/' })}>
            <img 
              src="/logo.png" 
              alt="Tripod Technologies Logo" 
              className="h-12 w-auto"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navigationItems.map((item) => (
              <a
                key={item.name}
                href={item.href + (item.anchor ? `#${item.anchor}` : '')}
                onClick={(e) => handleNavClick(e, item)}
                className="text-gray-700 hover:text-purple-600 transition-colors duration-200 font-medium cursor-pointer"
              >
                {item.name}
              </a>
            ))}
          </nav>

          {/* Contact Info - Hidden on web version */}
          <div className="hidden items-center space-x-4 text-sm text-gray-600">
            <div className="flex items-center space-x-1">
              <Phone className="w-4 h-4" />
              <span>+91-89832 30676</span>
            </div>
            <div className="flex items-center space-x-1">
              <Mail className="w-4 h-4" />
              <span>Info@Tripodtechnologies.com</span>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-3 min-w-[44px] min-h-[44px] rounded-lg hover:bg-gray-100 transition-colors duration-200"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            title={isMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <div className="px-4 pt-2 pb-3 space-y-1">
            {navigationItems.map((item) => (
              <a
                key={item.name}
                href={item.href + (item.anchor ? `#${item.anchor}` : '')}
                onClick={(e) => handleNavClick(e, item)}
                className="block px-3 py-2 text-gray-700 hover:text-purple-600 hover:bg-gray-50 rounded-md transition-colors duration-200 cursor-pointer"
              >
                {item.name}
              </a>
            ))}
            <div className="pt-4 mt-4 border-t border-gray-200 space-y-2">
              <div className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600">
                <Phone className="w-4 h-4" />
                <span>+91-89832 30676</span>
              </div>
              <div className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600">
                <Mail className="w-4 h-4" />
                <span>Info@Tripodtechnologies.com</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
